import {
  Card,
  Checkbox,
  Fieldset,
  Form,
  Grid,
  Input,
  Spacer,
  toast,
} from '@components/common'
import { Controller, FormProvider } from 'react-hook-form'
import { DEPRECIATION_PRODUCTION_REPORT_PATH, ERROR_REQUEST } from '@constants'
import ReportFilters from '../ReportFilters'
import { defaultReportFilterFields } from '../ReportFilters/report-filter-fields'
import { DeprecProductionReportParams } from '@api/models'
import { ActionButtons } from '../General'
import { useReport } from '@hooks'
import { useEffect } from 'react'
import { getCurrentYear } from '@utils/basic'
import { ReportsApi } from '@api'
import { reportResponseHandler } from '@utils/reports'
import { useMemorizeReport } from '../General/MemorizeReports/useMemorizeReport'
import MemorizeReports from '../General/MemorizeReports/MemorizeReport'
import { useLocation } from 'react-router-dom'
import { useFormMethods } from '../General/useFormMethods'

const defaultValues: DeprecProductionReportParams = {
  assetCompanyID: 0,
  filter: [],
  fromYr: getCurrentYear() - 1,
  toYr: getCurrentYear(),
  includeAssetDesc: false,
  fileFormat: 'pdf',
}

async function onSubmitHandler(values: DeprecProductionReportParams) {
  await ReportsApi.getInstance()
    .getDeprecProductionReport(values)
    .then(data => reportResponseHandler(data, values.fileFormat))
    .catch(() => toast.error(ERROR_REQUEST))
}

const DeprecProductionForm = () => {
  const location = useLocation()
  const locationState: any = location.state

  const {
    methods,
    isRetrievingTemplate,
    isEditTemplate,
    selectedTemplateDetails,
  } = useFormMethods(locationState, defaultValues)

  const {
    formState: { isSubmitting },
    control,
    setValue,
    register,
    watch,
  } = methods

  const { isLoading, isSuccess, isError, isNoCompanies, assetCompanyID } = useReport()

  const {
    handleMemorizeTemplateSubmit,
    isMemorizeTemplateModalOpen,
    closeMemorizeTemplateModal,
    triggerMemorizeTemplateModal,
    handleEditTemplateSubmit,
  } = useMemorizeReport({ methods, currentReportPath: DEPRECIATION_PRODUCTION_REPORT_PATH })

  const fileFormat = watch('fileFormat')

  useEffect(() => {
    if (isSuccess) {
      setValue('assetCompanyID', assetCompanyID)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, assetCompanyID])

  function getCheckBoxProps(field: any) {
    return {
      checked: field.value,
      onChange: field.onChange,
      ref: field.ref,
      disabled: isSubmitting,
    }
  }

  function getInputProps(name: keyof DeprecProductionReportParams) {
    return {
      ...register(name),
      disabled: isSubmitting,
    }
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  if (isError) {
    return <div>Error</div>
  }

  if (isNoCompanies) {
    return <div>No companies found</div>
  }

  return (
    <>
      <FormProvider {...methods}>
        <Form onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Card title={'Report Parameters'}>
            <Grid>
              <Grid.Item>
                <Fieldset legend="Fiscal Year Range">
                  <Grid>
                    <Grid.Item s={6}>
                      <Input {...getInputProps('fromYr')} type="number" label="From" />
                    </Grid.Item>
                    <Grid.Item s={6}>
                      <Input {...getInputProps('toYr')} type="number" label="To" />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item s={6}>
                <Fieldset legend={'Additional Columns to Include'}>
                  <Grid rowGap="mini">
                    <Grid.Item>
                      <Controller
                        control={control}
                        name="includeAssetDesc"
                        render={({ field }) => (
                          <Checkbox
                            label="Include asset description"
                            {...getCheckBoxProps(field)}
                          />
                        )}
                      />
                    </Grid.Item>
                  </Grid>
                </Fieldset>
              </Grid.Item>

              <Grid.Item>
                <ReportFilters
                  filtersTemplateData={
                    isRetrievingTemplate
                      ? locationState.templateDetails.templateData.filter
                      : []
                  }
                  filterFields={defaultReportFilterFields}
                  includesTotals={fileFormat === 'pdf'}
                />
              </Grid.Item>
            </Grid>
          </Card>
          <Spacer y="default" />
          <ActionButtons
            isSubmitting={isSubmitting}
            triggerMemorizeTemplateModal={triggerMemorizeTemplateModal}
            isEditTemplate={isEditTemplate}
          />
        </Form>
      </FormProvider>

      <MemorizeReports
        isMemorizeTemplateModalOpen={isMemorizeTemplateModalOpen}
        closeMemorizeTemplateModal={closeMemorizeTemplateModal}
        handleMemorizeTemplateSubmit={handleMemorizeTemplateSubmit}
        handleEditTemplateSubmit={handleEditTemplateSubmit}
        isEditTemplate={isEditTemplate}
        selectedTemplateDetails={selectedTemplateDetails}
      />
    </>
  )
}

export default DeprecProductionForm
